import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  HStack,
  Heading,
  useToast,
  Select,
  Textarea,
  Spinner,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import ApiResponse from "../../../services/ApiResponse";
import ProtectedRoute from "../../../services/ProtectedRoute";
import { getDecodedToken } from "../../../services/Token";
import { Noticia } from "../../../services/models/Noticia";
import ImageFileInput from "../../../components/LogoFileInput";

interface Tema {
  id: number;
  nombre: string;
}

const NoticiaAddPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const decoded = getDecodedToken();

  const [loading, setLoading] = useState(false);
  const [temas, setTemas] = useState<Tema[]>([]);
  const [loadingTemas, setLoadingTemas] = useState(true);

  // Refs
  const tituloRef = useRef<HTMLInputElement>(null);
  const resumenRef = useRef<HTMLTextAreaElement>(null);
  const contenidoRef = useRef<HTMLTextAreaElement>(null);
  const imagenRef = useRef<HTMLInputElement>(null);
  const visibilidadRef = useRef<HTMLSelectElement>(null);
  const estadoRef = useRef<HTMLSelectElement>(null);
  const temaRef = useRef<HTMLSelectElement>(null);
  const fechaPublicacionRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchTemas = async () => {
      try {
        const response = new ApiResponse<Tema[]>();
        await response.useFetch("temas", "GET");
        if (response.data) setTemas(response.data);
      } catch (error) {
        console.error("Error cargando temas", error);
      } finally {
        setLoadingTemas(false);
      }
    };

    fetchTemas();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!tituloRef.current?.value) {
      toast({
        title: "Error de validación",
        description: "El título es obligatorio",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!contenidoRef.current?.value) {
      toast({
        title: "Error de validación",
        description: "El contenido es obligatorio",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!temaRef.current?.value) {
      toast({
        title: "Error de validación",
        description: "Debes seleccionar un tema",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);

    try {
      // ✅ MISMO FORMATO QUE INSTITUCIÓN
      const payload = new FormData();

      payload.append("titulo", tituloRef.current.value);
      payload.append("resumen", resumenRef.current?.value || "");
      payload.append("contenido", contenidoRef.current.value);
      payload.append(
        "visibilidad",
        visibilidadRef.current?.value || "PUBLICA"
      );
      payload.append("estado", estadoRef.current?.value || "0");
      payload.append("autor_id", String(decoded?.usuario_id || 0));
      payload.append("tema_id", temaRef.current.value);

      if (fechaPublicacionRef.current?.value) {
        payload.append(
          "fecha_publicacion",
          fechaPublicacionRef.current.value
        );
      }

      // 📌 IMAGEN (idéntico a Institución)
      const imagenFile = imagenRef.current?.files?.[0];
      if (imagenFile) {
        payload.append("imagen", imagenFile);
      }

      const response = new ApiResponse<Noticia>();
      await response.useFetch("noticias", "POST", payload, true);

      if (response.responseCode !== 201) {
        throw new Error(response.error || "Error al crear la noticia");
      }

      toast({
        title: "Éxito",
        description: "Noticia creada correctamente",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      navigate("/dashboard/noticias");
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Error al crear la noticia",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  if (decoded?.rol_id !== 1 && decoded?.rol_id !== 2) {
    return (
      <Box p={5} bg="rgba(2, 54, 176, 0.9)" minH="100vh">
        <Heading color="white">No tienes permiso para acceder aquí</Heading>
      </Box>
    );
  }

  return (
    <ProtectedRoute roles={[1, 2]}>
      <Box p={5} bg="rgba(2, 54, 176, 0.9)" minH="100vh">
        <VStack spacing={6} maxW="1000px" mx="auto" align="stretch">
          <Heading color="white" textAlign="center">
            Crear Noticia
          </Heading>

          <Box bg="whiteAlpha.100" p={6} borderRadius="lg">
            <form onSubmit={handleSubmit}>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel color="white">Título</FormLabel>
                  <Input ref={tituloRef} bg="white" color="black" />
                </FormControl>

                <FormControl>
                  <FormLabel color="white">Resumen</FormLabel>
                  <Textarea
                    ref={resumenRef}
                    bg="white"
                    color="black"
                    rows={3}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color="white">Contenido</FormLabel>
                  <Textarea
                    ref={contenidoRef}
                    bg="white"
                    color="black"
                    rows={8}
                  />
                </FormControl>

                <ImageFileInput
                  inputRef={imagenRef}
                  label="Imagen de la noticia"
                />

                <FormControl isRequired>
                  <FormLabel color="white">Tema</FormLabel>
                  {loadingTemas ? (
                    <Spinner color="white" />
                  ) : (
                    <Select ref={temaRef} bg="white" color="black">
                      <option value="">Seleccionar tema</option>
                      {temas.map((tema) => (
                        <option key={tema.id} value={tema.id}>
                          {tema.nombre}
                        </option>
                      ))}
                    </Select>
                  )}
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color="white">Visibilidad</FormLabel>
                  <Select ref={visibilidadRef} bg="white" color="black">
                    <option value="PUBLICA">Pública</option>
                    <option value="INSTITUCIONAL">Institucional</option>
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color="white">Estado</FormLabel>
                  <Select
                    ref={estadoRef}
                    bg="white"
                    color="black"
                    defaultValue={0}
                  >
                    <option value={0}>Archivada</option>
                    <option value={1}>Publicada</option>
                    <option value={2}>Borrador</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel color="white">Fecha de publicación</FormLabel>
                  <Input
                    type="datetime-local"
                    ref={fechaPublicacionRef}
                    bg="white"
                    color="black"
                  />
                </FormControl>

                <HStack spacing={4} pt={4}>
                  <Button
                    colorScheme="blue"
                    type="submit"
                    isLoading={loading}
                    width="50%"
                  >
                    Crear Noticia
                  </Button>
                  <Button
                    colorScheme="gray"
                    onClick={() => navigate(-1)}
                    width="50%"
                  >
                    Cancelar
                  </Button>
                </HStack>
              </VStack>
            </form>
          </Box>
        </VStack>
      </Box>
    </ProtectedRoute>
  );
};

export default NoticiaAddPage;
